import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { COLORS } from "../../theme/colors";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { templateApi } from "../../api/client";
import { Button } from "../../components/ui";
import PageHeader from "../../components/PageHeader";
import MarketplaceViewer from "./viewer/MarketplaceViewer";
import MarketplaceTileGrid from "./iphone-grid/MarketplaceTileGrid";
import MarketplaceSearchBar, { applyTemplateFilter, readMarketplaceSort } from "./iphone-grid/MarketplaceSearchBar";
import MarketplaceDensityToggle, { readMarketplaceDensity } from "./iphone-grid/MarketplaceDensityToggle";
import MarketplaceCommandPalette from "./MarketplaceCommandPalette";
import MarketplaceFiltersPopover, { applyPopoverFilter } from "./MarketplaceFiltersPopover";
import TileHoverPreview from "../library/iphone-grid/TileHoverPreview";
import TileContextMenu from "../library/iphone-grid/TileContextMenu";
import { resolveTemplateIcon, categoryLabel } from "./iphone-grid/iconForTemplate";

// agent-experience-redesign-v1.md §20 Marketplace overhaul.
// i2-visual-identity-v2.md §10.1 A18 — list-mode escape hatch removed
// (reverses v1 §20.5 sign-off Q3 + §20.12 Q3). Marketplace is browse-+-
// install, not management. Toolbar parity with Library (⌘K palette,
// density toggle, filters popover, stats strip) made the list-mode
// fallback redundant; deleting it shrinks the maintenance surface and
// removes a divergent chrome path. Library keeps its own list mode
// because Library serves a different mental model (manage many owned
// agents).

const MarketplaceView = () => {
  const bp = useBreakpoint();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 50;  // backend max; chunked client-side render handles perf beyond that via scroll-based pagination
  // i2-visual-identity-v2 §10.1 A14 — sort persists in localStorage.marketplace.sort.
  const [searchBar, setSearchBar] = useState(() => ({
    search: "", category: "", sort: readMarketplaceSort(),
  }));
  // i2-visual-identity-v2 §10.1 A8 — density toggle (Comfy 96 / Compact 64).
  const [density, setDensity] = useState(() => readMarketplaceDensity());
  // i2-visual-identity-v2 §10.1 A12 — ⌘K command palette.
  const [paletteOpen, setPaletteOpen] = useState(false);
  // i2-visual-identity-v2 §10.1 A13 — filters popover state.
  const [popoverFilters, setPopoverFilters] = useState(null);
  const [hoverPreview, setHoverPreview] = useState(null);
  // Grace timer for hover-card stickiness: when the mouse leaves the
  // tile, we don't clear the preview immediately — we give the user
  // ~250ms to traverse the gap and enter the card. If they enter
  // the card we cancel the close; if they leave the card we re-arm
  // the timer. Without this the card vanishes mid-traverse and the
  // CTAs become unclickable.
  const hoverCloseTimerRef = useRef(null);
  const scheduleHoverClose = useCallback(() => {
    if (hoverCloseTimerRef.current) clearTimeout(hoverCloseTimerRef.current);
    hoverCloseTimerRef.current = setTimeout(() => {
      setHoverPreview(null);
      hoverCloseTimerRef.current = null;
    }, 250);
  }, []);
  const cancelHoverClose = useCallback(() => {
    if (hoverCloseTimerRef.current) {
      clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
  }, []);
  useEffect(() => () => {
    if (hoverCloseTimerRef.current) clearTimeout(hoverCloseTimerRef.current);
  }, []);
  const [contextMenu, setContextMenu] = useState(null);

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  // i2-visual-identity-v2 §10.1 A4 — `installTemplate` slot retired:
  // viewer carries Configure-tab state internally. Deep-link
  // `?install=<slug>` now opens the unified viewer on the Configure
  // tab instead of a separate modal.
  const [viewerInitialTab, setViewerInitialTab] = useState("overview");

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      // Server-side filtering still applies — pass current chip / search
      // through the templateApi so backend pagination + sort work even on
      // very large catalogs.
      const params = { page, page_size: pageSize, sort: searchBar.sort };
      if (searchBar.category) params.category = searchBar.category;
      if (searchBar.search) params.search = searchBar.search;
      const res = await templateApi.marketplace(params);
      if (res.success) {
        setTemplates(res.data?.templates || []);
        setTotal(res.data?.total || 0);
      }
    } catch {} finally { setLoading(false); }
  }, [page, searchBar.sort, searchBar.category, searchBar.search]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  // i2-visual-identity-v2 §10.1 A9 — auto-flip to compact when the
  // tenant has >50 templates AND the user has not explicitly chosen a
  // density. Mirrors Library's auto-flip rule. Runs once per template
  // fetch; the localStorage-override check makes the flip a one-shot
  // (subsequent loads honor the user's pick).
  useEffect(() => {
    let userOverridden = false;
    try { userOverridden = !!localStorage.getItem("marketplace.density"); } catch {}
    if (!userOverridden && templates.length > 50 && density === "comfortable") {
      setDensity("compact");
    }
  }, [templates.length]);

  // Reset to page 1 when filter changes; the searchBar onChange already
  // mutates these values so we just react to them.
  useEffect(() => { setPage(1); }, [searchBar.search, searchBar.category, searchBar.sort]);

  // i2-visual-identity-v2 §10.1 A12 — ⌘K / Ctrl+K hotkey opens the
  // command palette. Mirrors AgentLibraryView's wiring.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key || "").toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Deep-link from "Update available" pill: `/marketplace?install=<slug>`
  // opens the unified viewer on the Configure tab (per v2 §6.1).
  useEffect(() => {
    const installSlug = searchParams.get("install");
    if (!installSlug || templates.length === 0 || selectedTemplate) return;
    const match = templates.find(t => t.slug === installSlug || t.template === installSlug);
    if (match) {
      setSelectedTemplate(match);
      setViewerInitialTab("configure");
    }
  }, [searchParams, templates, selectedTemplate]);

  // Client-side double-filter: applies the same filter logic locally
  // so the chip strip + chunked render stay in sync even between
  // server fetches.
  const filtered = useMemo(
    () => applyPopoverFilter(applyTemplateFilter(templates, searchBar), popoverFilters),
    [templates, searchBar, popoverFilters],
  );


  const handleInstalled = () => {
    fetchTemplates();
    navigate("/library");
  };

  const handleDelete = (templateId) => {
    setTemplates(prev => prev.filter(t => t.id !== templateId));
    setSelectedTemplate(null);
  };

  const handleCopyShareLink = (template) => {
    const url = `${window.location.origin}/marketplace?install=${encodeURIComponent(template.slug || template.template)}`;
    navigator.clipboard?.writeText(url);
  };

  const handleExportConfig = (template) => {
    const blob = new Blob(
      [JSON.stringify(template.tree_snapshot || template, null, 2)],
      { type: "application/json" },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${template.slug || template.name || "template"}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleReport = (template) => {
    const subject = encodeURIComponent(`Report template: ${template.name}`);
    const body = encodeURIComponent(
      `Template ID: ${template.id}\nSlug: ${template.slug || template.template}\n\nReason:\n`,
    );
    window.location.href = `mailto:support@i2platform.ai?subject=${subject}&body=${body}`;
  };

  return (
    <div className="panel-scroll" style={{ flex: 1, padding: bp.isMobile ? "16px 12px 80px" : "24px 28px", overflowY: "auto" }}>
      <PageHeader title="Marketplace" />

      <div style={{ marginBottom: 16 }}>
        <MarketplaceSearchBar
          value={searchBar}
          onChange={setSearchBar}
          templates={templates}
          onOpenCommandPalette={() => setPaletteOpen(true)}
          trailing={
            <>
              <MarketplaceFiltersPopover
                value={popoverFilters}
                onChange={(next) => setPopoverFilters(next)}
              />
              <MarketplaceDensityToggle
                density={density}
                onChange={(d) => setDensity(d)}
              />
            </>
          }
        />
      </div>

      {/* Grid (the only view — list mode retired per v2 §10.1 A18) */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: COLORS.textMuted, fontSize: 13 }}>
          Loading templates…
        </div>
      ) : (
        <MarketplaceTileGrid
          templates={filtered}
          density={density}
          pinnedCategory={searchBar.category || null}
          onTileClick={(t) => setSelectedTemplate(t)}
          onTileInstall={(t) => { setSelectedTemplate(t); setViewerInitialTab("configure"); }}
          onTileContextMenu={(t, pt) => setContextMenu({ template: t, x: pt.x, y: pt.y })}
          onTileHover={(t, anchorRect) => {
            cancelHoverClose();
            setHoverPreview({ template: t, anchorRect });
          }}
          onTileHoverEnd={scheduleHoverClose}
          emptyState={
            <div
              data-testid="marketplace-empty-state"
              style={{ textAlign: "center", padding: 60, color: COLORS.textMuted, fontSize: 13 }}
            >
              {(searchBar.search || searchBar.category)
                ? "No templates match your filters"
                : "Install a template to populate your Library"}
            </div>
          }
        />
      )}

      {/* Unified MarketplaceViewer (i2-visual-identity-v2 §10.1 A4).
          Replaces the legacy split <TemplateDetailPanel> + <InstallTemplateModal>
          surfaces with a single drawer mirroring AgentViewer's contract:
          Overview / Configure / Reviews tabs + footer Install action. */}
      {selectedTemplate && (
        <MarketplaceViewer
          template={selectedTemplate}
          onClose={() => { setSelectedTemplate(null); setViewerInitialTab("overview"); }}
          onInstalled={handleInstalled}
          onDelete={handleDelete}
          initialTab={viewerInitialTab}
        />
      )}

      {/* Hover preview — reuses Library's primitive with template-
          flavored CTAs. (Grid is the only view post-v2 A18.) */}
      {hoverPreview && (
        <TemplateHoverPreviewAdapter
          template={hoverPreview.template}
          anchorRect={hoverPreview.anchorRect}
          onClose={() => { cancelHoverClose(); setHoverPreview(null); }}
          onCardEnter={cancelHoverClose}
          onCardLeave={scheduleHoverClose}
          onView={(t) => { cancelHoverClose(); setHoverPreview(null); setSelectedTemplate(t); }}
          onInstall={(t) => { cancelHoverClose(); setHoverPreview(null); setSelectedTemplate(t); setViewerInitialTab("configure"); }}
        />
      )}

      {/* Right-click menu — 5 items per §20.12 Q5. */}
      {contextMenu && (
        <TileContextMenu
          testId="marketplace-tile-context-menu"
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          items={[
            { key: "view",    label: "View details",
              onClick: () => setSelectedTemplate(contextMenu.template) },
            { key: "install", label: "Install",
              onClick: () => { setSelectedTemplate(contextMenu.template); setViewerInitialTab("configure"); } },
            { key: "share",   label: "Copy share link",
              onClick: () => handleCopyShareLink(contextMenu.template) },
            { divider: true },
            { key: "export",  label: "Export config",
              onClick: () => handleExportConfig(contextMenu.template) },
            { key: "report",  label: "Report", danger: true,
              onClick: () => handleReport(contextMenu.template) },
          ]}
        />
      )}

      {/* i2-visual-identity-v2 §10.1 A12 — ⌘K command palette. */}
      <MarketplaceCommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        templates={templates}
        onSelectTemplate={(t) => setSelectedTemplate(t)}
        onPublish={() => { /* TODO: wire publish flow when toolbar pill ships */ }}
        onBrowseFeatured={() => setSearchBar(s => ({ ...s, search: "Featured" }))}
        onOpenLibrary={() => navigate("/library")}
      />
    </div>
  );
};

// Adapter — TileHoverPreview was built for agents but we want
// template-flavored CTAs (View details / Install). Wrap rather
// than fork to keep the trigger logic + glass card layout shared.
function TemplateHoverPreviewAdapter({
  template, anchorRect,
  onClose, onCardEnter, onCardLeave,
  onView, onInstall,
}) {
  // Synthesize an "agent-shaped" object that TileHoverPreview can
  // render: name, description, status (forced "live" for green dot),
  // model, channels (empty so no chip).
  const { category, iconName } = resolveTemplateIcon(template);
  const synthAgent = {
    name: template.name,
    metadata_json: { description: template.description },
    // Marketplace tiles must NOT carry a status dot per §20.5; the
    // hover preview's dot uses status — we suppress it by passing
    // a sentinel that the preview component can detect. Quickest
    // fix: pass status="" so the dot helper falls back to textDim
    // and we'll style it transparent below by overriding via CSS
    // in a future polish pass. For v1 just let it render gray.
    status: "",
    version: template.schema_version ? String(template.schema_version) : null,
    flow_config: null,
    // Hover preview reads agent.model + agent.channels for chips.
    // Replace channels with the category label for marketplace.
    model: categoryLabel(template.category),
    channels: [],
    // Carry _category + _iconName so the preview's IconTile picks
    // them up (TileHoverPreview internally calls resolveAgentIcon
    // which reads metadata_json.icon — set it explicitly here so
    // the right gradient lands without forking the component).
    metadata_json_icon: { category, iconName },
  };
  // Patch metadata_json.icon for resolveAgentIcon to pick up the
  // template-flavored gradient (its resolver supports a
  // {category, iconName} object override).
  synthAgent.metadata_json = { ...synthAgent.metadata_json, icon: { category, iconName } };

  return (
    <TileHoverPreview
      agent={synthAgent}
      anchorRect={anchorRect}
      onClose={onClose}
      onCardEnter={onCardEnter}
      onCardLeave={onCardLeave}
      onOpen={() => onView(template)}
      onTest={() => onInstall(template)}
      openLabel="View"
      testLabel="Configure"
    />
  );
}

export default MarketplaceView;
